/**
 * BulaWrappedPage - Annual "Spotify Wrapped" style summary for Bula Coffee Club members
 * Shows yearly stats, achievements, and shareable graphics
 */
import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Coffee, Gift, Users, Camera, Award, Calendar,
  Share2, Download, ChevronRight, ChevronLeft, Loader2,
  Star, TrendingUp, Heart, Sparkles
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Helmet } from 'react-helmet-async';
import { toast } from 'sonner';
import api from '@/utils/apiClient';

// Slide component for wrapped presentation
const WrappedSlide = ({ children, bgColor = 'from-primary to-primary/80' }) => (
  <motion.div
    initial={{ opacity: 0, scale: 0.95 }}
    animate={{ opacity: 1, scale: 1 }}
    exit={{ opacity: 0, scale: 0.95 }}
    transition={{ duration: 0.4 }}
    className={`min-h-[500px] md:min-h-[600px] rounded-3xl bg-gradient-to-br ${bgColor} p-6 md:p-10 flex flex-col items-center justify-center text-center text-white relative overflow-hidden`}
  >
    {/* Decorative elements */}
    <div className="absolute top-0 left-0 w-32 h-32 bg-white/10 rounded-full -translate-x-1/2 -translate-y-1/2" />
    <div className="absolute bottom-0 right-0 w-48 h-48 bg-white/5 rounded-full translate-x-1/4 translate-y-1/4" />
    <div className="relative z-10 w-full">
      {children}
    </div>
  </motion.div>
);

// Stat display for slides
const BigStat = ({ value, label, icon: Icon }) => (
  <div className="mb-6">
    {Icon && <Icon className="w-12 h-12 md:w-16 md:h-16 mx-auto mb-4 opacity-80" />}
    <p className="text-5xl md:text-7xl font-bold mb-2">{value}</p>
    <p className="text-lg md:text-xl opacity-90">{label}</p>
  </div>
);

export default function BulaWrappedPage() {
  const [searchParams] = useSearchParams();
  const userIdFromUrl = searchParams.get('user_id');
  const yearFromUrl = searchParams.get('year');
  
  const [loading, setLoading] = useState(true);
  const [report, setReport] = useState(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  
  const currentYear = new Date().getFullYear();
  const year = yearFromUrl ? parseInt(yearFromUrl) : currentYear;
  
  // Load annual report
  useEffect(() => {
    if (!userIdFromUrl) {
      setLoading(false);
      return;
    }
    
    const loadReport = async () => {
      try {
        const res = await api.get(`/viral/reports/annual/${userIdFromUrl}?year=${year}`);
        setReport(res.data);
      } catch (err) {
        console.error('Failed to load report:', err);
        toast.error('Failed to load your Bula Wrapped');
      }
      setLoading(false);
    };
    
    loadReport();
  }, [userIdFromUrl, year]);
  
  // Navigation
  const nextSlide = () => setCurrentSlide(prev => Math.min(prev + 1, slides.length - 1));
  const prevSlide = () => setCurrentSlide(prev => Math.max(prev - 1, 0));
  
  // Share functionality
  const shareWrapped = async () => {
    const shareData = {
      title: `My ${year} Bula Wrapped`,
      text: `Check out my ${year} Bula Coffee Club stats! ${report?.coffee_pass?.total_coffees || 0} FREE coffees, ${report?.coffee_pass?.total_savings || 0} saved! Get yours at bulaccinocafe.com/bula-coffee-club`,
      url: `https://bulaccinocafe.com/bula-coffee-club`
    };
    
    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch (err) {
        navigator.clipboard?.writeText(shareData.text + ' ' + shareData.url);
        toast.success('Copied to clipboard!');
      }
    } else {
      navigator.clipboard?.writeText(shareData.text + ' ' + shareData.url);
      toast.success('Copied to clipboard!');
    }
  };
  
  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }
  
  // No user ID or report
  if (!userIdFromUrl || !report) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-background px-4 text-center">
        <Sparkles className="w-16 h-16 text-muted-foreground mb-4" />
        <h1 className="text-xl font-heading mb-2">Bula Wrapped Not Found</h1>
        <p className="text-muted-foreground mb-4">
          Access your Bula Wrapped from your account dashboard.
        </p>
        <Link to="/bula-coffee-club">
          <Button>
            <Coffee className="w-4 h-4 mr-2" /> Get Coffee Pass
          </Button>
        </Link>
      </div>
    );
  }
  
  // Build slides based on available data
  const slides = [
    // Intro slide
    {
      id: 'intro',
      bg: 'from-emerald-600 to-teal-700',
      content: (
        <>
          <Sparkles className="w-16 h-16 mx-auto mb-6 animate-pulse" />
          <h1 className="text-3xl md:text-5xl font-heading mb-4">
            Your {year} Bula Wrapped
          </h1>
          <p className="text-xl opacity-90 mb-8">
            {report.name?.split(' ')[0]}, here's your year in coffee
          </p>
          <p className="text-sm opacity-70">Tap to continue</p>
        </>
      )
    }
  ];
  
  // Coffee Pass slide (if has data)
  if (report.coffee_pass) {
    slides.push({
      id: 'coffees',
      bg: 'from-amber-600 to-orange-700',
      content: (
        <>
          <BigStat 
            value={report.coffee_pass.total_coffees} 
            label="FREE Coffees" 
            icon={Coffee}
          />
          <p className="text-lg opacity-90">
            That's {report.coffee_pass.unique_days} days of delicious coffee!
          </p>
        </>
      )
    });
    
    slides.push({
      id: 'savings',
      bg: 'from-green-600 to-emerald-700',
      content: (
        <>
          <BigStat 
            value={report.coffee_pass.total_savings} 
            label="Total Value" 
            icon={Gift}
          />
          <p className="text-xl mb-4">
            You paid <span className="font-bold">$95</span> for your pass
          </p>
          <p className="text-3xl font-bold">
            That's {report.coffee_pass.roi_multiplier}x your money!
          </p>
        </>
      )
    });
  }
  
  // Shares slide
  if (report.shares?.total > 0) {
    slides.push({
      id: 'shares',
      bg: 'from-purple-600 to-pink-600',
      content: (
        <>
          <BigStat 
            value={report.shares.total} 
            label="Photos Shared" 
            icon={Camera}
          />
          {report.shares.top_platform && (
            <p className="text-lg opacity-90">
              You loved sharing on <span className="capitalize font-bold">{report.shares.top_platform}</span>
            </p>
          )}
        </>
      )
    });
  }
  
  // Points slide
  if (report.points?.earned > 0) {
    slides.push({
      id: 'points',
      bg: 'from-blue-600 to-indigo-700',
      content: (
        <>
          <BigStat 
            value={report.points.earned} 
            label="Points Earned" 
            icon={Star}
          />
          <p className="text-lg opacity-90">
            You're a <span className="capitalize font-bold">{report.tier}</span> member!
          </p>
        </>
      )
    });
  }
  
  // Referrals slide
  if (report.referrals?.total > 0) {
    slides.push({
      id: 'referrals',
      bg: 'from-rose-600 to-red-700',
      content: (
        <>
          <BigStat 
            value={report.referrals.total} 
            label="Friends Referred" 
            icon={Users}
          />
          <p className="text-lg opacity-90">
            You spread the Bula love!
          </p>
          {report.referrals.friends_names?.length > 0 && (
            <p className="text-sm mt-4 opacity-70">
              Including {report.referrals.friends_names.slice(0, 3).join(', ')}
            </p>
          )}
        </>
      )
    });
  }
  
  // Final summary slide
  slides.push({
    id: 'summary',
    bg: 'from-primary to-primary/80',
    content: (
      <>
        <Award className="w-16 h-16 mx-auto mb-6" />
        <h2 className="text-2xl md:text-4xl font-heading mb-6">
          What a Year, {report.name?.split(' ')[0]}!
        </h2>
        <div className="grid grid-cols-2 gap-4 max-w-sm mx-auto mb-8">
          {report.coffee_pass && (
            <div className="bg-white/20 rounded-xl p-4">
              <p className="text-2xl font-bold">{report.coffee_pass.total_coffees}</p>
              <p className="text-xs opacity-80">Coffees</p>
            </div>
          )}
          <div className="bg-white/20 rounded-xl p-4">
            <p className="text-2xl font-bold">{report.shares?.total || 0}</p>
            <p className="text-xs opacity-80">Shares</p>
          </div>
          <div className="bg-white/20 rounded-xl p-4">
            <p className="text-2xl font-bold">{report.points?.earned || 0}</p>
            <p className="text-xs opacity-80">Points</p>
          </div>
          <div className="bg-white/20 rounded-xl p-4">
            <p className="text-2xl font-bold">{report.referrals?.total || 0}</p>
            <p className="text-xs opacity-80">Referrals</p>
          </div>
        </div>
        <Button 
          onClick={shareWrapped}
          className="bg-white text-primary hover:bg-white/90"
        >
          <Share2 className="w-4 h-4 mr-2" /> Share Your Wrapped
        </Button>
      </>
    )
  });
  
  return (
    <>
      <Helmet>
        <title>{`Your ${year} Bula Wrapped | Bulaccino Cafe`}</title>
        <meta name="robots" content="noindex" />
      </Helmet>
      
      <div className="min-h-screen bg-background py-8 px-4">
        <div className="max-w-lg mx-auto">
          {/* Header */}
          <div className="text-center mb-6">
            <p className="text-sm text-muted-foreground">
              Slide {currentSlide + 1} of {slides.length}
            </p>
          </div>
          
          {/* Slide container */}
          <div 
            className="cursor-pointer" 
            onClick={currentSlide < slides.length - 1 ? nextSlide : undefined}
          >
            <AnimatePresence mode="wait">
              <WrappedSlide key={slides[currentSlide].id} bgColor={slides[currentSlide].bg}>
                {slides[currentSlide].content}
              </WrappedSlide>
            </AnimatePresence>
          </div>
          
          {/* Navigation */}
          <div className="flex items-center justify-between mt-6">
            <Button 
              variant="outline" 
              size="sm"
              onClick={prevSlide}
              disabled={currentSlide === 0}
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            
            {/* Progress dots */}
            <div className="flex items-center gap-1">
              {slides.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setCurrentSlide(i)}
                  className={`w-2 h-2 rounded-full transition-all ${
                    i === currentSlide 
                      ? 'bg-primary w-4' 
                      : 'bg-muted-foreground/30'
                  }`}
                />
              ))}
            </div>
            
            <Button 
              variant="outline" 
              size="sm"
              onClick={nextSlide}
              disabled={currentSlide === slides.length - 1}
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
          
          {/* Back link */}
          <div className="text-center mt-8">
            <Link 
              to={`/rewards?user_id=${userIdFromUrl}`}
              className="text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              Back to Dashboard
            </Link>
          </div>
        </div>
      </div>
    </>
  );
}
